<script>
	/* eslint-disable no-unused-vars */
	// NPM Modules
	import { tick } from 'svelte';
	import dayjs from 'dayjs';
	// Stores
	import ACLStore from '@/Stores/ACLStore.js';
	import PromotionUserStore from '@/Stores/PromotionUserStore.js';
	import CampaignStore from '@/Stores/CampaignStore.js';
	// Components
	import Loading from '@/Components/Loading.svelte';
	import Pagination from '@/Components/Pagination.svelte';
	import Icon from '@/Components/Icon.svelte';
	import LoadingButton from '@/Components/LoadingButton.svelte';
	// Services
	import { getUrlParameter, setUrlParameter, paginationBuilder, NumberFormat } from '@/Services/Helpers.js';
	import { CurrencyFormat } from '@/Services/Helpers';
	// Partials
	import ViewCampaign from '@/Partials/Campaigns/_Show.svelte';

	export let user;

	$: links =
		$PromotionUserStore && $PromotionUserStore.count() && paginationBuilder(PromotionUserStore.getPagination());

	let filter = {};
	let pageNo = getUrlParameter('page') || 1;
	let pageSize = 25;
	let loading = user && getPromotions();
	let viewCampaignId;
	let viewCampaignObject;
	let showViewCampaign = false;

	function changePage(e) {
		if (e) {
			pageNo = e.detail.page;
			loading = getPromotions();
			setUrlParameter('page', pageNo);
		}
	}

	function getPromotions() {
		return PromotionUserStore.getPromotionsForUser(
			user.id,
			{
				size: pageSize,
				number: pageNo
			},
			filter
		);
	}

	async function showCampaign(campaign) {
		if (!campaign.campaign_id) {
			return;
		}

		viewCampaignId = campaign.id;
		viewCampaignObject = campaign;
		showViewCampaign = true;
		await tick();
		window.scrollTo(0, 0);
	}
</script>

{#await loading}
	<Loading />
{:then finishedLoading}
	{#if $ACLStore && ACLStore.granted('campaigns.read') && showViewCampaign && viewCampaignId}
		<ViewCampaign
			bind:open={showViewCampaign}
			bind:campaignId={viewCampaignId}
			bind:campaign={viewCampaignObject}
			account_managers={CampaignStore.getAccountManagers()}
		/>
	{/if}
	{#if $ACLStore && ACLStore.granted('promotions.read')}
		<div class="p-2 mx-4 bg-gray-100 rounded md:flex md:flex-col">
			<div class="flex items-center px-2 bg-dark-grey text-light">
				<h2 class="flex-1 py-2 text-xl italic font-sans-condensed">Promotion Offers</h2>
			</div>
			<div class="mt-2 bg-light overflow-x-auto">
				<div class="table w-full">
					<div class="table-header-group">
						<div class="table-row text-sm text-center bg-grey text-light">
							<div class="table-cell py-2 pl-2 text-left">Promo Name</div>
							<div class="table-cell py-2 pl-2 text-left">Published</div>
							<div class="table-cell py-2 pl-2 text-left">Expiry</div>
							<div class="table-cell py-2 pl-2 text-left">Used</div>
							<div class="table-cell py-2 pl-2 text-left">Promotion Offer</div>
							<div class="table-cell py-2 pl-2 text-left">Awarded By</div>
							<div class="table-cell py-2 pl-2 text-left">Awarded Date</div>
							<div class="table-cell py-2 pl-2 text-left">Awarded</div>
							<div class="table-cell py-2 pl-2 text-left">Award Expires</div>
						</div>
					</div>
					<div class="table-row-group">
						{#if !$PromotionUserStore.count()}
							<div class="table-row text-sm text-center">
								<div class="table-cell px-2 py-1.5 text-left border-b">No Promotions</div>
							</div>
						{:else}
							{#each $PromotionUserStore.all() as promotionUser}
								<div class="table-row text-sm text-center">
									<div
										class="table-cell px-2 py-1.5 text-left border-b align-middle"
										class:text-gray-600={promotionUser.promotion.name}
									>
										{promotionUser.promotion.name}
									</div>
									<div
										class="table-cell px-2 py-1.5 text-left border-b min-w-24 align-middle"
										class:text-gray-600={promotionUser.promotion.published_at && !promotionUser.promotion.never_expire}
									>
										{#if promotionUser.promotion.never_expire}
											Never Expires
										{:else}
											{dayjs(promotionUser.promotion.published_at).format('D MMM YYYY HH:mm')}
										{/if}
									</div>
									<div
										class="table-cell px-2 py-1.5 text-left border-b min-w-24 align-middle"
										class:text-gray-600={promotionUser.promotion.expired_at && !promotionUser.promotion.never_expire}
									>
										{#if promotionUser.promotion.never_expire}
											Never Expires
										{:else}
											{dayjs(promotionUser.promotion.expired_at).format('D MMM YYYY, HH:mm')}
										{/if}
									</div>
									<div
										class="table-cell px-2 py-1.5 text-left border-b min-w-16 align-middle"
										class:text-gray-600={promotionUser.used}
									>
										{#if promotionUser.used}
											<Icon name="circle-tick" classes="block w-6 h-6 text-green-500 inline" />
										{:else}
											<Icon name="circle-x" classes="block w-6 h-6 text-gray-500 inline" />
										{/if}
									</div>
									<div
										class="table-cell px-2 py-1.5 text-left border-b min-w-56 align-middle"
										class:text-gray-600={promotionUser.rules}
									>
										<div>
											{#if promotionUser.max_bonus_amount || (promotionUser.promotion.rules?.bonus_rules && promotionUser.promotion.rules?.bonus_rules?.max_bonus_amount && promotionUser.promotion.rules?.bonus_rules?.max_bonus_amount[user.rating.id]) || promotionUser.promotion.rules.max_bonus_amount}
												<div>
													<span class="font-semibold">Max Bonus Amount:</span>
													<span class=""
														>{CurrencyFormat(
															promotionUser.max_bonus_amount ||
																(promotionUser.promotion.rules?.bonus_rules &&
																	promotionUser.promotion.rules?.bonus_rules?.max_bonus_amount[user.rating.id]) ||
																promotionUser.promotion.rules.max_bonus_amount
														)}</span
													>
												</div>
											{/if}
											{#if (promotionUser.promotion.rules?.bonus_rules && promotionUser.promotion.rules?.bonus_rules?.percentage) || promotionUser.promotion.rules.percentage}
												<div>
													<span class="font-semibold">Percentage:</span>
													<span class=""
														>{NumberFormat(
															(promotionUser.promotion.rules?.bonus_rules &&
																promotionUser.promotion.rules?.bonus_rules?.percentage) ||
																promotionUser.promotion.rules.percentage,
															true
														)}%</span
													>
												</div>
											{/if}
											{#if (promotionUser.promotion.rules?.bonus_rules && promotionUser.promotion.rules?.bonus_rules?.valid_days) || promotionUser.promotion.rules.valid_days}
												<div>
													<span class="font-semibold">Bonus Valid For:</span>
													<span class=""
														>{NumberFormat(
															(promotionUser.promotion.rules?.bonus_rules &&
																promotionUser.promotion.rules?.bonus_rules?.valid_days) ||
																promotionUser.promotion.rules.valid_days,
															true
														)} days</span
													>
												</div>
											{/if}
										</div>
									</div>
									<div
										class="table-cell px-2 py-1.5 text-left border-b min-w-24 align-middle"
										class:text-gray-600={promotionUser.bonus}
									>
										{#if promotionUser.promotion?.campaign?.campaign_id}
											{#if $ACLStore && ACLStore.granted('campaigns.read')}
												<LoadingButton
													on:click={() => showCampaign(promotionUser.promotion.campaign)}
													className="btn btn-primary"
												>
													Campaign {promotionUser.promotion.campaign.campaign_id}
												</LoadingButton>
											{:else}
												<div
													class="flex flex-row justify-center items-center px-2 py-1 text-xs text-center bg-yellow-500 text-white rounded w-32"
												>
													Campaign {promotionUser.promotion.campaign.campaign_id}
												</div>
											{/if}
										{:else if promotionUser?.adminUser?.id}
											<div
												class="flex flex-row justify-center items-center px-2 py-1 text-xs text-center bg-green-500 text-white rounded w-32"
											>
												{promotionUser.adminUser.full_name}<br /> (Admin #{promotionUser.adminUser.id})
											</div>
										{:else}
											<div
												class="flex flex-row justify-center items-center px-2 py-1 text-xs text-center bg-gray-100 text-dark rounded w-32"
											>
												System
											</div>
										{/if}
									</div>
									<div
										class="table-cell px-2 py-1.5 text-left border-b min-w-24 align-middle"
										class:text-gray-600={promotionUser.bonus}
									>
										{#if promotionUser.bonus.created_at}
											{dayjs(promotionUser.bonus.created_at).format('D MMM YYYY HH:mm')}
										{:else}
											-
										{/if}
									</div>
									<div
										class="table-cell px-2 py-1.5 text-left border-b min-w-24 align-middle"
										class:text-gray-600={promotionUser.bonus}
									>
										{#if promotionUser.bonus.amount}
											{CurrencyFormat(promotionUser.bonus.amount)}
										{:else}
											-
										{/if}
									</div>
									<div
										class="table-cell px-2 py-1.5 text-left border-b min-w-24 align-middle"
										class:text-gray-600={promotionUser.bonus}
									>
										{#if promotionUser.bonus.expired_at}
											{dayjs(promotionUser.bonus.expired_at).format('D MMM YYYY HH:mm')}
										{:else}
											-
										{/if}
									</div>
								</div>
							{/each}
						{/if}
					</div>
				</div>
			</div>
			{#if $PromotionUserStore.count()}
				<Pagination {links} on:click={changePage} className="py-4 bg-gray-100 md:sticky md:bottom-0" />
			{/if}
		</div>
	{/if}
{/await}
